import { apiHooks, setQueriesData } from '../../../hooks/useRestAPIHooks';
import { CompositeReportDefinitionDetail, CreateItem } from '../models';
import { useNotify } from '../../../hooks';
import { defineMessages, useIntl } from 'react-intl';
import { useInvalidateTemplatingItemQueries } from './useManageTemplatingItemQueries';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SELECTED_TEMPLATING_ITEM_QUERY_KEY } from './useSelectedTemplatingItem';
import { CompositeReportDefinitionDetailRawData } from '../types';

const messages = defineMessages({
  compositeReportDefinitionSaved: {
    id: 'compositeReportDefinitionSaved',
    defaultMessage: 'Composite report template saved',
  },
  compositeReportDefinitionSaveFailed: {
    id: 'compositeReportDefinitionSaveFailed',
    defaultMessage: 'Failed to save composite report template',
  },
});

export const useSaveCompositeReportDefinition = () => {
  const { formatMessage } = useIntl();
  const invalidateCompositeReportDefinition =
    useInvalidateTemplatingItemQueries();
  const optimisticUpdate = useOptimisticUpdate();
  const [notify] = useNotify();

  const { mutateAsync: saveCompositeReportDefinition } =
    apiHooks.useAPIMutation('PATCH /v1/ai-templates/:id');

  const save = useCallback(
    async (
      compositeReportDefinition: CompositeReportDefinitionDetail,
      update: Partial<
        Omit<CreateItem<CompositeReportDefinitionDetail>, 'path'>
      >,
      { showNotification = true }: { showNotification?: boolean } = {},
    ) => {
      try {
        const updatedCompositeReportDefinition = {
          ...compositeReportDefinition,
          ...update,
        };
        optimisticUpdate(updatedCompositeReportDefinition);
        await saveCompositeReportDefinition(updatedCompositeReportDefinition);
        if (showNotification) {
          notify(
            formatMessage(messages.compositeReportDefinitionSaved),
            'success',
          );
        }
        await invalidateCompositeReportDefinition(compositeReportDefinition);
      } catch {
        if (showNotification) {
          notify(
            formatMessage(messages.compositeReportDefinitionSaveFailed),
            'error',
          );
        }
      }
    },
    [
      optimisticUpdate,
      saveCompositeReportDefinition,
      invalidateCompositeReportDefinition,
      notify,
      formatMessage,
    ],
  );

  return {
    save,
  };
};

function useOptimisticUpdate() {
  const queryClient = useQueryClient();

  const optimisticUpdate = useCallback(
    (crd: CompositeReportDefinitionDetail) => {
      setQueriesData(queryClient, {
        route: SELECTED_TEMPLATING_ITEM_QUERY_KEY,
        predicate: (payload: { id: string }) => payload.id === crd.id,
        dataTransformer: (
          oldData:
            | { template: CompositeReportDefinitionDetailRawData }
            | undefined,
        ) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            template: {
              ...oldData.template,
              sections: crd.sections,
            },
          };
        },
      });
    },
    [queryClient],
  );

  return optimisticUpdate;
}
